import { getNotificacoes, marcarComoLida } from "@spark/services/notificacoes";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { Collapse } from "react-bootstrap";
import NotificationsIcon from "mdi-react/NotificationsIcon";
import { TopbarBack, TopbarButton } from "./BasicTopbarComponents";
import {
  TopbarCollapse,
  TopbarCollapseButton,
  TopbarCollapseContent,
  TopbarCollapseTitle,
  TopbarCollapseTitleWrap,
} from "./CollapseTopbarComponents";
import styled from "styled-components";
import { useAuth } from "../../../context/AuthContext";
import { useNotificacoesAlert } from "../../../context/NotificacoesAlertContext";
import { TIPO_ATRASO_CRITICO, ordenarNotificacoesListagem } from "@spark/services/notificacoes/alerta";
import { colorAccent, colorBorder, colorGray } from "@spark/ui-components/theme";

const PAGE_SIZE = 20;
const VISIBLE_HEIGHT = 400;
const FILTRO_NAO_LIDAS = { Lida: false };

const TopbarNotification = () => {
  const history = useHistory();
  const { isAuthenticated } = useAuth();
  const { notificacaoNovaVersao } = useNotificacoesAlert();
  const enableNotificacoes = process.env.REACT_APP_NOTIFICACOES_ENABLED === "true";
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [notificacoes, setNotificacoes] = useState([]);
  const [totalRegistros, setTotalRegistros] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalNaoLidas, setTotalNaoLidas] = useState(0);
  const scrollRef = useRef(null);
  const loadingMoreRef = useRef(false);

  const atualizarContador = useCallback(async () => {
    if (!isAuthenticated || !enableNotificacoes) {
      return;
    }
    try {
      const response = await getNotificacoes({ Limit: 1, Offset: 0, ...FILTRO_NAO_LIDAS });
      if (typeof response?.totalNaoLidas === "number") {
        setTotalNaoLidas(response.totalNaoLidas);
      }
    } catch {
      // Mantém contador atual em caso de falha.
    }
  }, [isAuthenticated, enableNotificacoes]);

  const carregarPrimeiraPagina = useCallback(async () => {
    if (!isAuthenticated || !enableNotificacoes) {
      return;
    }
    try {
      setLoading(true);
      const response = await getNotificacoes({ Limit: PAGE_SIZE, Offset: 0, ...FILTRO_NAO_LIDAS });
      const lista = ordenarNotificacoesListagem(
        Array.isArray(response?.registros) ? response.registros : []
      );
      setNotificacoes(lista);
      setTotalRegistros(typeof response?.totalRegistros === "number" ? response.totalRegistros : lista.length);
      if (typeof response?.totalNaoLidas === "number") {
        setTotalNaoLidas(response.totalNaoLidas);
      }
      if (scrollRef.current) {
        scrollRef.current.scrollTop = 0;
      }
    } catch {
      setNotificacoes([]);
      setTotalRegistros(0);
      setTotalNaoLidas(0);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, enableNotificacoes]);

  const carregarMais = useCallback(async () => {
    if (!isAuthenticated || !enableNotificacoes || loadingMoreRef.current) {
      return;
    }
    if (notificacoes.length >= totalRegistros) {
      return;
    }

    loadingMoreRef.current = true;
    setLoadingMore(true);
    try {
      const response = await getNotificacoes({
        Limit: PAGE_SIZE,
        Offset: notificacoes.length,
        ...FILTRO_NAO_LIDAS,
      });
      const novos = Array.isArray(response?.registros) ? response.registros : [];
      if (novos.length > 0) {
        setNotificacoes((prev) => {
          const idsExistentes = new Set(prev.map((item) => item.Id));
          const filtrados = novos.filter((item) => !idsExistentes.has(item.Id));
          return ordenarNotificacoesListagem([...prev, ...filtrados]);
        });
      }
      if (typeof response?.totalRegistros === "number") {
        setTotalRegistros(response.totalRegistros);
      }
    } catch {
      // Mantém lista atual em caso de falha.
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [isAuthenticated, enableNotificacoes, notificacoes.length, totalRegistros]);

  // Limpa o estado ao deslogar.
  useEffect(() => {
    if (!isAuthenticated) {
      setNotificacoes([]);
      setTotalNaoLidas(0);
      setTotalRegistros(0);
    }
  }, [isAuthenticated]);

  // Contador de nao lidas: uma unica chamada por login e por nova notificacao
  // (sem depender de isCollapsed para nao refazer ao abrir/fechar o dropdown).
  useEffect(() => {
    if (!isAuthenticated || !enableNotificacoes) {
      return;
    }
    atualizarContador();
  }, [notificacaoNovaVersao, isAuthenticated, enableNotificacoes, atualizarContador]);

  // Lista aberta: carrega a primeira pagina ao abrir e a cada nova notificacao.
  // carregarPrimeiraPagina tambem atualiza totalNaoLidas (mesma resposta).
  useEffect(() => {
    if (!isAuthenticated || !enableNotificacoes || !isCollapsed) {
      return;
    }
    carregarPrimeiraPagina();
  }, [notificacaoNovaVersao, isCollapsed, isAuthenticated, enableNotificacoes, carregarPrimeiraPagina]);

  const handleScroll = (event) => {
    const elemento = event.currentTarget;
    const proximoDoFim = elemento.scrollTop + elemento.clientHeight >= elemento.scrollHeight - 80;
    if (proximoDoFim) {
      carregarMais();
    }
  };

  const handleLer = async (event, notificacao) => {
    event.stopPropagation();
    if (notificacao.Lida) {
      return;
    }
    try {
      await marcarComoLida(notificacao.Id);
      setNotificacoes((prev) => prev.filter((n) => n.Id !== notificacao.Id));
      setTotalRegistros((prev) => Math.max(0, prev - 1));
      setTotalNaoLidas((prev) => Math.max(0, prev - 1));
    } catch {}
  };

  const handleMarcarTodasLidas = async () => {
    try {
      await marcarComoLida("todas");
      setNotificacoes([]);
      setTotalRegistros(0);
      setTotalNaoLidas(0);
    } catch {}
  };

  const obterLinkDestino = (notificacao) => {
    const dados = notificacao?.DadosAdicionais;
    if (!dados) return null;
    if (typeof dados === "string") {
      try {
        const parsed = JSON.parse(dados);
        return parsed?.link_destino || null;
      } catch {
        return null;
      }
    }
    return dados?.link_destino || null;
  };

  const handleVisualizar = (notificacao) => {
    const linkDestino = obterLinkDestino(notificacao);
    if (linkDestino) {
      history.push(linkDestino);
    } else if (notificacao.AcompanhamentoId) {
      history.push(`/pages/acompanhamentos/detalhes/${notificacao.AcompanhamentoId}`);
    }

    setIsCollapsed(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));

    if (diffInHours < 1) {
      return "Agora mesmo";
    }
    if (diffInHours < 24) {
      return `${diffInHours}h atras`;
    }
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const collapseNotification = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <TopbarCollapse>
      <TopbarButton type="button" onClick={collapseNotification}>
        <NotificationsIcon />
        {totalNaoLidas > 0 && (
          <NotificationCount>
            {totalNaoLidas > 99 ? "99+" : totalNaoLidas}
          </NotificationCount>
        )}
      </TopbarButton>
      {isCollapsed && <TopbarBack aria-label="topbar__back" type="button" onClick={collapseNotification} />}

      <Collapse in={isCollapsed}>
        <NotificationDropdownContent>
          <TopbarCollapseTitleWrap>
            <TopbarCollapseTitle>Notificacoes</TopbarCollapseTitle>
            {totalNaoLidas > 0 && (
              <TopbarCollapseButton type="button" onClick={handleMarcarTodasLidas}>
                Marcar todas como lidas
              </TopbarCollapseButton>
            )}
          </TopbarCollapseTitleWrap>
          {loading ? (
            <NotificationEmptyState>Carregando...</NotificationEmptyState>
          ) : notificacoes.length === 0 ? (
            <NotificationEmptyState>Nenhuma notificacao</NotificationEmptyState>
          ) : (
            <NotificacoesScrollContainer ref={scrollRef} onScroll={handleScroll}>
              {notificacoes.map((notificacao) => (
                <NotificationItem
                  key={notificacao.Id}
                  $critical={notificacao.TipoNotificacao === TIPO_ATRASO_CRITICO}
                >
                  <NotificationBody type="button" onClick={() => handleVisualizar(notificacao)}>
                    <NotificationHeader>
                      <NotificationTitle $critical={notificacao.TipoNotificacao === TIPO_ATRASO_CRITICO}>
                        {notificacao.Titulo}
                      </NotificationTitle>
                      <NotificationDate>{formatDate(notificacao.DataCriacao)}</NotificationDate>
                    </NotificationHeader>
                    <NotificationMessage>{notificacao.Mensagem || ""}</NotificationMessage>
                  </NotificationBody>
                  <LerButton type="button" onClick={(event) => handleLer(event, notificacao)}>
                    Ler
                  </LerButton>
                </NotificationItem>
              ))}
              {loadingMore && <LoadingMoreText>Carregando mais...</LoadingMoreText>}
            </NotificacoesScrollContainer>
          )}
        </NotificationDropdownContent>
      </Collapse>
    </TopbarCollapse>
  );
};

export default TopbarNotification;

const NotificationDropdownContent = styled(TopbarCollapseContent)`
  width: 380px;

  @media screen and (max-width: 576px) {
    width: 300px;
  }

  @media screen and (max-width: 320px) {
    width: 280px;
  }
`;

const NotificationCount = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  background-color: #e0536f;
  color: white;
  font-size: 10px;
  padding: 2px 4px;
  border-radius: 10px;
  min-width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NotificacoesScrollContainer = styled.div`
  max-height: ${VISIBLE_HEIGHT}px;
  overflow-y: auto;
`;

const NotificationItem = styled.div`
  display: flex;
  align-items: stretch;
  gap: 8px;
  padding: 12px 12px 12px 14px;
  border-bottom: 1px solid ${colorBorder};
  background: ${(props) =>
    props.$critical ? "rgba(224, 83, 111, 0.08)" : "rgba(112, 187, 253, 0.08)"};
`;

const NotificationBody = styled.button`
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  padding: 0;
  text-align: left;
  cursor: pointer;
`;

const NotificationHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
`;

const NotificationTitle = styled.p`
  margin: 0;
  flex: 1;
  font-size: 13px;
  line-height: 1.35;
  font-weight: 600;
  color: ${(props) => (props.$critical ? "#c0392b" : colorAccent)};
  word-break: break-word;
`;

const NotificationDate = styled.span`
  flex-shrink: 0;
  font-size: 10px;
  line-height: 1.3;
  color: ${colorGray};
  white-space: nowrap;
`;

const NotificationMessage = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 1.45;
  color: ${colorGray};
  word-break: break-word;
  white-space: pre-wrap;
`;

const LerButton = styled.button`
  flex-shrink: 0;
  align-self: center;
  border: 1px solid ${colorAccent};
  background: transparent;
  color: ${colorAccent};
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
  padding: 8px 10px;
  border-radius: 4px;
  cursor: pointer;
  transition: 0.2s;

  &:hover {
    background: ${colorAccent};
    color: #fff;
  }
`;

const NotificationEmptyState = styled.div`
  padding: 16px 15px;
  font-size: 12px;
  color: ${colorGray};
`;

const LoadingMoreText = styled.div`
  padding: 8px 15px;
  font-size: 12px;
  color: ${colorGray};
  text-align: center;
`;
